import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, query, orderBy, doc, getDoc, limit, startAfter, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { useSelector } from 'react-redux';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2';
import AccessControl from '../Components/AccessControl.tsx';
import { PageName } from '../../../enums.ts';
import { 
    CircularProgress, 
    IconButton, 
    MenuItem, 
    Select, 
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Typography,
    Tooltip,
    Button
} from '@mui/material';
import { 
    ChevronLeft, 
    ChevronRight,
    Receipt,
    AccountBalance,
    MoneyOff,
    Event,
    LocalOffer,
    ShoppingCart,
    Subscriptions,
    ExpandMore,
    Search,
    ShowChart,
    Close
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

interface LedgerEntry {
    id: string;
    action: 'RECEIVABLE' | 'ADJUSTMENT' | 'RECEIVED' | 'CANCELLED' | 'SETTLED';
    amount: number;
    createdAt: any;
    currency: string;
    customerId?: string;
    customerName?: string;
    orderId?: string;
    subscriptionId?: string;
    subscriptionName?: string;
    paymentStatus: 'PAID' | 'PENDING' | 'CANCELLED';
    paymentMode?: string;
    platformFees?: number;
    transferAmount?: number;
    type: 'subscription' | 'product' | 'event' | 'offer' | 'payout';
    transactionTime?: any;
    settlementTime?: any;
}

interface MonthlyStats {
    month?: string;
    year?: string;
    totalPaid: number;
    totalPending: number;
    totalSettled: number;
    totalWriteoff: number;
    platformFees: number;
    totalLedgerEntries: number;
    totalPayout: number;
    updatedAt?: any;
    summaryType?: string;
    byType?: {
        [key: string]: {
            totalPaid: number;
            totalPending: number;
            totalSettled: number;
            totalWriteoff: number;
            platformFees: number;
            count?: {
                paid: number;
                due: number;
                settled: number;
                transactions: number;
            };
        };
    };
    count?: {
        [key: string]: {
            paid: number;
            due: number;
            settled: number;
            transactions: number;
            payoutCount: number;
        };
    };
}

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

// Type icon mapping constant to avoid duplication
const TYPE_ICON_CONFIG = {
    subscription: { 
        Icon: Subscriptions, 
        colorClasses: { '500': 'text-purple-500', '600': 'text-purple-600' }
    },
    event: { 
        Icon: Event, 
        colorClasses: { '500': 'text-blue-500', '600': 'text-blue-600' }
    },
    offer: { 
        Icon: LocalOffer, 
        colorClasses: { '500': 'text-green-500', '600': 'text-green-600' }
    },
    product: { 
        Icon: ShoppingCart, 
        colorClasses: { '500': 'text-orange-500', '600': 'text-orange-600' }
    },
    payout: { 
        Icon: AccountBalance, 
        colorClasses: { '500': 'text-red-500', '600': 'text-red-600' }
    }
} as const;

const getTypeIcon = (type: string, size: 'small' | 'medium' | 'large' = 'small', colorIntensity: '500' | '600' = '600') => {
    const typeLower = type.toLowerCase();
    const config = TYPE_ICON_CONFIG[typeLower as keyof typeof TYPE_ICON_CONFIG];
    
    if (config) {
        const { Icon, colorClasses } = config;
        return <Icon className={colorClasses[colorIntensity]} fontSize={size} />;
    }
    
    const grayClasses = { '500': 'text-gray-500', '600': 'text-gray-600' } as const;
    return <Receipt className={grayClasses[colorIntensity]} fontSize={size} />;
};

const getStatusStyles = (paymentStatus: string) => {
    switch (paymentStatus) {
        case 'PAID':
            return { 
                bg: 'bg-green-50', 
                text: 'text-green-700',
                badge: 'bg-green-100 text-green-800',
                label: 'Paid',
                icon: '✓'
            };
        case 'PENDING':
            return { 
                bg: 'bg-orange-50', 
                text: 'text-orange-700',
                badge: 'bg-orange-100 text-orange-800',
                label: 'UnPaid',
                icon: '⏱'
            };
        case 'CANCELLED':
            return { 
                bg: 'bg-red-50', 
                text: 'text-red-700',
                badge: 'bg-red-100 text-red-800',
                label: 'Written Off',
                icon: '✗'
            };
        default:
            return { 
                bg: 'bg-gray-50', 
                text: 'text-gray-700',
                badge: 'bg-gray-100 text-gray-800',
                label: paymentStatus,
                icon: '•'
            };
    }
};

const formatCurrency = (amount: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
    }).format(amount);
};

const ENTRIES_PER_PAGE = 500;

type FilterType = 'all' | 'PAID' | 'PENDING' | 'CANCELLED';

export default function Ledger() {
    const serviceProviderId = useSelector(selectEffectiveUID);
    const [entries, setEntries] = useState<LedgerEntry[]>([]);
    const [monthlyStats, setMonthlyStats] = useState<MonthlyStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [selectedYear, setSelectedYear] = useState(`FY${new Date().getFullYear()}`);
    const [selectedMonth, setSelectedMonth] = useState<string>(''); // Will be set to latest month when available
    const [availableYears, setAvailableYears] = useState<string[]>([]);
    const [availableMonths, setAvailableMonths] = useState<string[]>([]);
    const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [filterType, setFilterType] = useState<FilterType>('all');
    
    // Historical trends state
    const [showTrendsModal, setShowTrendsModal] = useState(false);
    const [historicalData, setHistoricalData] = useState<MonthlyStats[]>([]);
    const [loadingHistorical, setLoadingHistorical] = useState(false);
    const [selectedTrendsYear, setSelectedTrendsYear] = useState<string>('all');

    const loadAvailableYears = useCallback(async () => {
        if (!serviceProviderId) return;
        try {
            const ledgerRef = collection(db, `ServiceProviders/${serviceProviderId}/Ledger`);
            const snapshot = await getDocs(ledgerRef);
            const years = snapshot.docs.map(doc => doc.id);
            setAvailableYears(years.sort().reverse());
            if (years.length > 0 && !years.includes(selectedYear)) {
                setSelectedYear(years[0]);
            }
        } catch (error) {
            console.error('Error loading years:', error);
        }
    }, [serviceProviderId, selectedYear]);

    const loadAvailableMonths = useCallback(async () => {
        if (!serviceProviderId || !selectedYear) return;
        try {
            const monthsRef = collection(db, `ServiceProviders/${serviceProviderId}/Ledger/${selectedYear}/months`);
            const snapshot = await getDocs(monthsRef);
            const months = snapshot.docs.map(doc => doc.id);
            
            // Sort months chronologically (by monthNames array index)
            const sortedMonths = months.sort((a, b) => {
                const indexA = monthNames.indexOf(a);
                const indexB = monthNames.indexOf(b);
                return indexB - indexA; // Reverse order (latest first)
            });
            
            setAvailableMonths(sortedMonths);
        } catch (error) {
            console.error('Error loading months:', error);
        }
    }, [serviceProviderId, selectedYear]);

    // Helper function to map Firestore documents to LedgerEntry objects
    const mapDocsToEntries = useCallback((docs: QueryDocumentSnapshot<DocumentData>[]): LedgerEntry[] => {
        return docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as LedgerEntry[];
    }, []);

    // Helper function to update pagination state
    const updatePaginationState = useCallback((snapshot: any) => {
        if (snapshot.docs.length === ENTRIES_PER_PAGE) {
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setHasMore(true);
        } else {
            setLastVisible(null);
            setHasMore(false);
        }
    }, []);

    // Load historical monthly data for trends
    const loadHistoricalData = useCallback(async () => {
        if (!serviceProviderId) return;
        
        setLoadingHistorical(true);
        try {
            const allHistoricalData: MonthlyStats[] = [];
            
            // Get all years
            const ledgerRef = collection(db, `ServiceProviders/${serviceProviderId}/Ledger`);
            const yearsSnapshot = await getDocs(ledgerRef);
            
            // Loop through each year
            for (const yearDoc of yearsSnapshot.docs) {
                const year = yearDoc.id;
                
                // Get all months for this year
                const monthsRef = collection(db, `ServiceProviders/${serviceProviderId}/Ledger/${year}/months`);
                const monthsSnapshot = await getDocs(monthsRef);
                
                // Get monthly stats for each month
                for (const monthDoc of monthsSnapshot.docs) {
                    const monthName = monthDoc.id;
                    const monthData = monthDoc.data() as MonthlyStats;
                    
                    // Add month and year info
                    allHistoricalData.push({
                        ...monthData,
                        month: monthName,
                        year: year
                    });
                }
            }
            
            // Sort by year and month (chronologically)
            allHistoricalData.sort((a, b) => {
                // First compare years
                const yearA = a.year || '';
                const yearB = b.year || '';
                if (yearA !== yearB) {
                    return yearA.localeCompare(yearB);
                }
                
                // If same year, compare months
                const monthA = monthNames.indexOf(a.month || '');
                const monthB = monthNames.indexOf(b.month || '');
                return monthA - monthB;
            });
            
            setHistoricalData(allHistoricalData);
        } catch (error) {
            console.error('Error loading historical data:', error);
        } finally {
            setLoadingHistorical(false);
        }
    }, [serviceProviderId]);
    
    // Open trends modal and load data
    const handleShowTrends = useCallback(async () => {
        setShowTrendsModal(true);
        if (historicalData.length === 0) {
            await loadHistoricalData();
        }
    }, [historicalData.length, loadHistoricalData]);

    // Get unique years from historical data
    const availableTrendsYears = useMemo(() => {
        const years = new Set<string>();
        historicalData.forEach(data => {
            if (data.year) {
                years.add(data.year);
            }
        });
        return Array.from(years).sort().reverse();
    }, [historicalData]);

    // Filter historical data by selected year
    const filteredHistoricalData = useMemo(() => {
        if (selectedTrendsYear === 'all') {
            return historicalData;
        }
        return historicalData.filter(data => data.year === selectedTrendsYear);
    }, [historicalData, selectedTrendsYear]);

    // Set default year when historical data loads
    useEffect(() => {
        if (historicalData.length > 0 && selectedTrendsYear === 'all') {
            // Set to most recent year or 'all' if only one year
            if (availableTrendsYears.length > 0) {
                setSelectedTrendsYear(availableTrendsYears[0]);
            }
        }
    }, [historicalData, availableTrendsYears, selectedTrendsYear]);

    const loadEntries = useCallback(async () => {
        if (!serviceProviderId || !selectedYear || !selectedMonth) return;
        setLoading(true);
        setEntries([]);
        setLastVisible(null);
        setHasMore(false);
        
        try {
            // Fetch monthly stats from the month document
            const monthDocRef = doc(
                db, 
                `ServiceProviders/${serviceProviderId}/Ledger/${selectedYear}/months/${selectedMonth}`
            );
            const monthDocSnap = await getDoc(monthDocRef);
            
            if (monthDocSnap.exists()) {
                const stats = monthDocSnap.data() as MonthlyStats;
                setMonthlyStats(stats);
            } else {
                setMonthlyStats(null);
            }

            // Fetch first batch of entries with pagination
            const entriesRef = collection(
                db, 
                `ServiceProviders/${serviceProviderId}/Ledger/${selectedYear}/months/${selectedMonth}/entries`
            );
            const q = query(
                entriesRef, 
                orderBy('createdAt', 'desc'),
                limit(ENTRIES_PER_PAGE)
            );
            const snapshot = await getDocs(q);
            const loadedEntries = mapDocsToEntries(snapshot.docs);
            
            setEntries(loadedEntries);
            updatePaginationState(snapshot);
        } catch (error) {
            console.error('Error loading entries:', error);
        } finally {
            setLoading(false);
        }
    }, [serviceProviderId, selectedYear, selectedMonth, mapDocsToEntries, updatePaginationState]);

    const loadMoreEntries = async () => {
        if (!serviceProviderId || !selectedYear || !selectedMonth || !lastVisible || loadingMore) return;
        
        setLoadingMore(true);
        try {
            const entriesRef = collection(
                db, 
                `ServiceProviders/${serviceProviderId}/Ledger/${selectedYear}/months/${selectedMonth}/entries`
            );
            const q = query(
                entriesRef,
                orderBy('createdAt', 'desc'),
                startAfter(lastVisible),
                limit(ENTRIES_PER_PAGE)
            );
            const snapshot = await getDocs(q);
            const newEntries = mapDocsToEntries(snapshot.docs);
            
            setEntries(prev => [...prev, ...newEntries]);
            updatePaginationState(snapshot);
        } catch (error) {
            console.error('Error loading more entries:', error);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadAvailableYears();
    }, [loadAvailableYears]);

    useEffect(() => {
        if (selectedYear) {
            loadAvailableMonths();
        }
    }, [selectedYear, loadAvailableMonths]);
    
    // Also ensure latest month is selected when months become available
    useEffect(() => {
        if (availableMonths.length > 0) {
            // If no month selected or selected month is not available, select the latest
            if (!selectedMonth || !availableMonths.includes(selectedMonth)) {
                // Select the latest month (first in the sorted array)
                setSelectedMonth(availableMonths[0]);
            }
        }
    }, [availableMonths, selectedMonth]);

    useEffect(() => {
        if (selectedYear && selectedMonth) {
            setFilterType('all'); // Reset filter when month changes
            loadEntries();
        }
    }, [selectedYear, selectedMonth, loadEntries]);

    const summary = useMemo(() => {
        // Use pre-computed stats from Firestore if available
        if (monthlyStats) {
            return {
                totalReceivable: monthlyStats.totalPaid + monthlyStats.totalPending,
                totalReceived: monthlyStats.totalPaid,
                totalSettled: monthlyStats.totalSettled,
                totalCancelled: monthlyStats.totalWriteoff,
                pendingAmount: monthlyStats.totalPending,
                netAmount: monthlyStats.totalPaid - monthlyStats.totalSettled,
                platformFees: monthlyStats.platformFees,
                totalPayout: monthlyStats.totalPayout
            };
        }

        // Fallback: calculate from entries if stats not available
        let totalReceivable = 0;
        let totalReceived = 0;
        let totalSettled = 0;
        let totalCancelled = 0;
        let pendingAmount = 0;

        entries.forEach(entry => {
            if (entry.action === 'RECEIVABLE') {
                totalReceivable += entry.amount;
                if (entry.paymentStatus === 'PENDING') {
                    pendingAmount += entry.amount;
                }
            }
            if (entry.action === 'RECEIVED' && entry.paymentStatus === 'PAID') {
                totalReceived += entry.transferAmount || entry.amount;
            }
            if (entry.action === 'SETTLED') {
                totalSettled += entry.amount;
            }
            if (entry.paymentStatus === 'CANCELLED') {
                totalCancelled += entry.amount;
            }
        });

        return {
            totalReceivable,
            totalReceived,
            totalSettled,
            totalCancelled,
            pendingAmount,
            netAmount: totalReceived - totalSettled,
            platformFees: 0,
            totalPayout: 0
        };
    }, [entries, monthlyStats]);

    const navigateMonth = (direction: 'prev' | 'next') => {
        const currentIndex = availableMonths.indexOf(selectedMonth);
        if (direction === 'prev' && currentIndex > 0) {
            setSelectedMonth(availableMonths[currentIndex - 1]);
        } else if (direction === 'next' && currentIndex < availableMonths.length - 1) {
            setSelectedMonth(availableMonths[currentIndex + 1]);
        }
    };

    const filteredEntries = useMemo(() => {
        if (filterType === 'all') return entries;
        
        // Filter mapping: UI labels → Database values
        // "UnPaid" (UI) → paymentStatus === 'PENDING' (DB)
        // "Paid" (UI) → paymentStatus === 'PAID' (DB)
        // "Written Off" (UI) → paymentStatus === 'CANCELLED' (DB)
        
        if (filterType === 'PENDING') {
            // UnPaid filter: Map to DB value 'PENDING'
            // Shows all entries where paymentStatus === 'PENDING' (unpaid/pending transactions)
            return entries.filter(entry => entry.paymentStatus === 'PENDING');
        }
        
        if (filterType === 'PAID') {
            // Paid filter: Map to DB value 'PAID'
            // Shows all entries where paymentStatus === 'PAID' (paid transactions)
            return entries.filter(entry => entry.paymentStatus === 'PAID');
        }
        
        if (filterType === 'CANCELLED') {
            // Written Off filter: Map to DB value 'CANCELLED'
            return entries.filter(entry => entry.paymentStatus === 'CANCELLED');
        }
        
        // Fallback: filter by paymentStatus matching filterType
        return entries.filter(entry => entry.paymentStatus === filterType);
    }, [entries, filterType]);

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        // If today, show time
        if (dateOnly.getTime() === today.getTime()) {
            return date.toLocaleTimeString('en-IN', { 
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        
        // If this year, show day and month
        if (date.getFullYear() === now.getFullYear()) {
            return date.toLocaleDateString('en-IN', { 
                day: 'numeric', 
                month: 'short'
            });
        }
        
        // Otherwise show full date
        return date.toLocaleDateString('en-IN', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric'
        });
    };

    return (
        <AccessControl pageName={PageName.Ledger}>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
                <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Ledger Book</h1>
                            <p className="text-gray-600">Track all your financial transactions</p>
                        </div>
                        
                        <div className="flex items-center gap-4">
                            {/* Year Selector */}
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Year</InputLabel>
                                <Select
                                    value={selectedYear}
                                    label="Year"
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                >
                                    {availableYears.map(year => (
                                        <MenuItem key={year} value={year}>{year}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Month Navigation */}
                            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                                <IconButton 
                                    size="small" 
                                    onClick={() => navigateMonth('prev')}
                                    disabled={availableMonths.indexOf(selectedMonth) === 0}
                                >
                                    <ChevronLeft />
                                </IconButton>
                                <span className="font-semibold text-gray-700 min-w-[100px] text-center">
                                    {selectedMonth}
                                </span>
                                <IconButton 
                                    size="small" 
                                    onClick={() => navigateMonth('next')}
                                    disabled={availableMonths.indexOf(selectedMonth) === availableMonths.length - 1}
                                >
                                    <ChevronRight />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Breakdown by Type */}
                {monthlyStats && monthlyStats.byType && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Breakdown by Type</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {Object.entries(monthlyStats.byType).map(([type, stats]) => {
                                return (
                                    <Tooltip key={type} title={`${type.charAt(0).toUpperCase() + type.slice(1)} transactions`} arrow>
                                        <Card className="hover:shadow-xl transition-shadow">
                                            <CardContent className="p-4">
                                                <div className="flex items-center gap-2 mb-3">
                                                    {getTypeIcon(type, 'medium', '500')}
                                                    <Typography variant="subtitle2" className="font-semibold capitalize">
                                                        {type}
                                                    </Typography>
                                                </div>
                                                <div className="space-y-1 text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Paid:</span>
                                                        <span className="font-semibold text-green-600">
                                                            {formatCurrency(stats.totalPaid || 0)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Pending:</span>
                                                        <span className="font-semibold text-orange-600">
                                                            {formatCurrency(stats.totalPending || 0)}
                                                        </span>
                                                    </div>
                                                    {monthlyStats.count && monthlyStats.count[type] && (
                                                        <div className="flex justify-between pt-1 border-t">
                                                            <span className="text-gray-500 text-xs">Transactions:</span>
                                                            <span className="text-xs font-medium">
                                                                {monthlyStats.count[type].transactions || 0}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Compact Summary Cards */}
                <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                    <div className="grid grid-cols-4 gap-4 divide-x divide-gray-200">
                        <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Paid</p>
                            <p className="text-2xl font-bold text-green-600">
                                {formatCurrency(summary.totalReceived)}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">UnPaid</p>
                            <p className="text-2xl font-bold text-orange-600">
                                {formatCurrency(summary.pendingAmount)}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Total Settled</p>
                            <p className="text-2xl font-bold text-purple-600">
                                {formatCurrency(summary.totalSettled)}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Payment Gateway Charges</p>
                            <p className="text-2xl font-bold text-blue-600">
                                {formatCurrency(summary.platformFees)}
                            </p>
                        </div>
                    </div>
                    
                    {/* Historical Trends Button */}
                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-center">
                        <Button
                            variant="outlined"
                            startIcon={<ShowChart />}
                            onClick={handleShowTrends}
                            className="text-purple-600 border-purple-600 hover:bg-purple-50"
                        >
                            View Historical Trends
                        </Button>
                    </div>
                    {summary.totalCancelled > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 flex justify-center gap-6 text-sm">
                            <div>
                                <span className="text-gray-500">Write-offs: </span>
                                <span className="font-semibold text-red-600">{formatCurrency(summary.totalCancelled)}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Entries List */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    {/* Header with filters */}
                    <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {selectedMonth} {selectedYear}
                                </h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {filterType === 'all' 
                                        ? `${filteredEntries.length} of ${monthlyStats?.totalLedgerEntries || entries.length} transactions`
                                        : `${filteredEntries.length} ${filterType === 'PAID' ? 'paid' : filterType === 'PENDING' ? 'unpaid' : 'written off'}`
                                    }
                                </p>
                            </div>
                        </div>
                        
                        {/* Filter Buttons */}
                        <div className="flex gap-2 flex-wrap">
                            <button
                                onClick={() => setFilterType('all')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                    filterType === 'all'
                                        ? 'bg-blue-100 text-blue-700 shadow-sm'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setFilterType('PAID')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                    filterType === 'PAID'
                                        ? 'bg-green-100 text-green-700 shadow-sm'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                <span className="text-xs">✓</span>
                                Paid
                            </button>
                            <button
                                onClick={() => setFilterType('PENDING')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                    filterType === 'PENDING'
                                        ? 'bg-orange-100 text-orange-700 shadow-sm'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                <span className="text-xs">⏱</span>
                                UnPaid
                            </button>
                            <button
                                onClick={() => setFilterType('CANCELLED')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                    filterType === 'CANCELLED'
                                        ? 'bg-red-100 text-red-700 shadow-sm'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                <span className="text-xs">✗</span>
                                Written Off
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex flex-col justify-center items-center py-20">
                            <CircularProgress size={60} />
                            <p className="text-gray-500 mt-4">Loading transactions...</p>
                        </div>
                    ) : availableYears.length === 0 ? (
                        <div className="text-center py-20 px-4">
                            <Search className="text-gray-300 mb-4" style={{ fontSize: 80 }} />
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Ledger Data Yet</h3>
                            <p className="text-gray-500 text-base max-w-md mx-auto">
                                Your ledger is empty. Transactions will appear here once you start receiving payments.
                            </p>
                        </div>
                    ) : filteredEntries.length === 0 ? (
                        <div className="text-center py-20 px-4">
                            <MoneyOff className="text-gray-300 mb-4" style={{ fontSize: 80 }} />
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">
                                {filterType === 'all' ? 'No Transactions' : `No ${filterType === 'PAID' ? 'Paid' : filterType === 'PENDING' ? 'UnPaid' : 'Written Off'} Transactions`}
                            </h3>
                            <p className="text-gray-500 text-base">
                                {filterType === 'all' 
                                    ? `No transactions found for ${selectedMonth} ${selectedYear}`
                                    : `No ${filterType === 'PAID' ? 'paid' : filterType === 'PENDING' ? 'unpaid' : 'written off'} transactions in ${selectedMonth}`
                                }
                            </p>
                            {filterType !== 'all' ? (
                                <button 
                                    onClick={() => setFilterType('all')}
                                    className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                >
                                    View all transactions
                                </button>
                            ) : (
                                <p className="text-gray-400 text-sm mt-2">
                                    Try selecting a different month or year
                                </p>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="divide-y divide-gray-100">
                                {filteredEntries.map((entry) => {
                                const statusStyles = getStatusStyles(entry.paymentStatus);
                                return (
                                    <div 
                                        key={entry.id}
                                        className="p-4 hover:bg-gray-50 transition-all duration-150 border-l-4 border-transparent hover:border-gray-300"
                                    >
                                        <div className="flex items-center justify-between gap-4">
                                            {/* Left Section: Icon + Details */}
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                {/* Type Icon */}
                                                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                                                    {getTypeIcon(entry.type)}
                                                </div>

                                                {/* Details */}
                                                <div className="flex-1 min-w-0">
                                                    {/* Customer Name + Status */}
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <h3 className="font-semibold text-gray-900 text-base truncate">
                                                            {entry.customerName || 'N/A'}
                                                        </h3>
                                                        {/* Status Badge - compact */}
                                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${statusStyles.badge}`}>
                                                            {statusStyles.label}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Transaction Description + Date */}
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <p className="text-gray-600 truncate">
                                                            {entry.subscriptionName || entry.type || 'Transaction'}
                                                        </p>
                                                        <span className="text-gray-400 text-xs flex-shrink-0">
                                                            • {formatDate(entry.createdAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right Section: Amount */}
                                            <div className="text-right flex-shrink-0">
                                                <div className={`text-lg font-bold ${statusStyles.text}`}>
                                                    {entry.paymentStatus === 'PAID' ? '+ ' : ''}
                                                    {formatCurrency(entry.amount, entry.currency)}
                                                </div>
                                                {entry.transferAmount && entry.transferAmount !== entry.amount && entry.paymentStatus === 'PAID' && (
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        ₹{entry.transferAmount.toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Load More Button */}
                        {hasMore && (
                            <div className="p-6 border-t border-gray-200 bg-gray-50">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    fullWidth
                                    onClick={loadMoreEntries}
                                    disabled={loadingMore}
                                    startIcon={loadingMore ? <CircularProgress size={20} /> : <ExpandMore />}
                                    className="hover:bg-blue-50"
                                >
                                    {loadingMore ? 'Loading...' : `Load More (${ENTRIES_PER_PAGE} at a time)`}
                                </Button>
                                <p className="text-center text-xs text-gray-500 mt-2">
                                    Showing {entries.length} of {monthlyStats?.totalLedgerEntries || 'many'} total entries
                                </p>
                            </div>
                        )}
                        
                        {/* End of List Message */}
                        {!hasMore && filteredEntries.length > 0 && (
                            <div className="p-4 text-center text-sm text-gray-500 border-t border-gray-200">
                                <p>You've reached the end • {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} shown</p>
                            </div>
                        )}
                    </>
                    )}
                </div>
                </div>
            </div>

            {/* Historical Trends Modal */}
            {showTrendsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl z-10">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Historical Trends</h2>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {selectedTrendsYear === 'all' 
                                            ? 'Monthly financial overview across all periods'
                                            : `Monthly financial overview for ${selectedTrendsYear}`
                                        }
                                    </p>
                                </div>
                                <IconButton
                                    onClick={() => setShowTrendsModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <Close />
                                </IconButton>
                            </div>
                            
                            {/* Year Filter */}
                            {availableTrendsYears.length > 0 && (
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-700">Filter by Year:</span>
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Year</InputLabel>
                                        <Select
                                            value={selectedTrendsYear}
                                            label="Year"
                                            onChange={(e) => setSelectedTrendsYear(e.target.value)}
                                        >
                                            <MenuItem value="all">All Years</MenuItem>
                                            {availableTrendsYears.map(year => (
                                                <MenuItem key={year} value={year}>{year}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {selectedTrendsYear !== 'all' && (
                                        <span className="text-xs text-gray-500">
                                            {filteredHistoricalData.length} {filteredHistoricalData.length === 1 ? 'month' : 'months'} shown
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Modal Content */}
                        <div className="p-6">
                            {loadingHistorical ? (
                                <div className="flex flex-col items-center justify-center py-20">
                                    <CircularProgress size={60} />
                                    <p className="text-gray-500 mt-4">Loading historical data...</p>
                                </div>
                            ) : historicalData.length === 0 ? (
                                <div className="text-center py-20">
                                    <ShowChart className="text-gray-300 mx-auto mb-4" style={{ fontSize: 64 }} />
                                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No Historical Data</h3>
                                    <p className="text-gray-500">Historical trends will appear here once you have multiple months of data.</p>
                                </div>
                            ) : filteredHistoricalData.length === 0 ? (
                                <div className="text-center py-20">
                                    <ShowChart className="text-gray-300 mx-auto mb-4" style={{ fontSize: 64 }} />
                                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No Data for Selected Year</h3>
                                    <p className="text-gray-500 mb-4">No data available for {selectedTrendsYear}. Try selecting a different year.</p>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setSelectedTrendsYear('all')}
                                    >
                                        View All Years
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Revenue Trends Chart */}
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trends</h3>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={filteredHistoricalData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                <XAxis 
                                                    dataKey="month" 
                                                    stroke="#6b7280"
                                                    fontSize={12}
                                                    tickFormatter={(value) => value?.substring(0, 3) || ''}
                                                />
                                                <YAxis 
                                                    stroke="#6b7280"
                                                    fontSize={12}
                                                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                                                />
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        padding: '12px'
                                                    }}
                                                    formatter={(value: number) => formatCurrency(value)}
                                                    labelFormatter={(label) => `${label}`}
                                                />
                                                <Legend />
                                                <Bar 
                                                    dataKey="totalPaid" 
                                                    fill="#10b981" 
                                                    name="Paid"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Bar 
                                                    dataKey="totalPending" 
                                                    fill="#f59e0b" 
                                                    name="Pending"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Settlement Trends Chart */}
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Settlement Trends</h3>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={filteredHistoricalData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                <XAxis 
                                                    dataKey="month" 
                                                    stroke="#6b7280"
                                                    fontSize={12}
                                                    tickFormatter={(value) => value?.substring(0, 3) || ''}
                                                />
                                                <YAxis 
                                                    stroke="#6b7280"
                                                    fontSize={12}
                                                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                                                />
                                                <RechartsTooltip
                                                    contentStyle={{
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        padding: '12px'
                                                    }}
                                                    formatter={(value: number) => formatCurrency(value)}
                                                    labelFormatter={(label) => `${label}`}
                                                />
                                                <Legend />
                                                <Bar 
                                                    dataKey="totalSettled" 
                                                    fill="#3b82f6" 
                                                    name="Settled"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Bar 
                                                    dataKey="totalPayout" 
                                                    fill="#8b5cf6" 
                                                    name="Payout"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Transaction Count and Payment Gateway Charges */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction Count</h3>
                                            <ResponsiveContainer width="100%" height={250}>
                                                <BarChart data={filteredHistoricalData}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                    <XAxis 
                                                        dataKey="month" 
                                                        stroke="#6b7280"
                                                        fontSize={12}
                                                        tickFormatter={(value) => value?.substring(0, 3) || ''}
                                                    />
                                                    <YAxis 
                                                        stroke="#6b7280"
                                                        fontSize={12}
                                                    />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '8px',
                                                            padding: '12px'
                                                        }}
                                                        labelFormatter={(label) => `${label}`}
                                                    />
                                                    <Bar 
                                                        dataKey="totalLedgerEntries" 
                                                        fill="#8b5cf6" 
                                                        name="Transactions"
                                                        radius={[4, 4, 0, 0]}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>

                                        <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-lg font-semibold text-gray-700">Payment Gateway Charges</h3>
                                                <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                    Secure & Reliable
                                                </div>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-4">Ensuring secure payment processing for all transactions</p>
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart data={filteredHistoricalData}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                                                    <XAxis 
                                                        dataKey="month" 
                                                        stroke="#9ca3af"
                                                        fontSize={11}
                                                        tickFormatter={(value) => value?.substring(0, 3) || ''}
                                                    />
                                                    <YAxis 
                                                        stroke="#9ca3af"
                                                        fontSize={11}
                                                        tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                                                    />
                                                    <RechartsTooltip
                                                        contentStyle={{
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '8px',
                                                            padding: '12px',
                                                            fontSize: '12px'
                                                        }}
                                                        formatter={(value: number) => formatCurrency(value)}
                                                        labelFormatter={(label) => `${label}`}
                                                    />
                                                    <Bar 
                                                        dataKey="platformFees" 
                                                        fill="#94a3b8" 
                                                        name="Payment Gateway Charges"
                                                        radius={[4, 4, 0, 0]}
                                                        opacity={0.7}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* Historical Data Table */}
                                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Summary Table</h3>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b border-gray-200">
                                                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Month</th>
                                                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Paid</th>
                                                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Pending</th>
                                                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Settled</th>
                                                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Transactions</th>
                                                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Payment Gateway Charges</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredHistoricalData.map((data, index) => (
                                                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                                                            <td className="py-3 px-4 font-medium text-gray-900">
                                                                {data.month} {data.year}
                                                            </td>
                                                            <td className="py-3 px-4 text-right text-green-600 font-semibold">
                                                                {formatCurrency(data.totalPaid)}
                                                            </td>
                                                            <td className="py-3 px-4 text-right text-orange-600 font-semibold">
                                                                {formatCurrency(data.totalPending)}
                                                            </td>
                                                            <td className="py-3 px-4 text-right text-blue-600 font-semibold">
                                                                {formatCurrency(data.totalSettled)}
                                                            </td>
                                                            <td className="py-3 px-4 text-right text-gray-700">
                                                                {data.totalLedgerEntries}
                                                            </td>
                                                            <td className="py-3 px-4 text-right text-gray-700">
                                                                {formatCurrency(data.platformFees)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AccessControl>
    );
}

